import React, { useState, useRef, useEffect, ReactNode, Suspense, useMemo } from 'react';
import { SummarizerConfig } from '../../../../types/summarizer';
import { Summary } from '../../../../types';
import { SafeImage } from '@components/ui/safe-image';
import { Button } from '@components/ui';
import { CopyButton } from '@components/ui/CopyButton';
import { ExportDropdown } from '@components/ui';
import PDFViewer from '../../../ui/PDFViewer';
import { AlignLeft, Brain, FileText, Layers, MessageSquare, Plus, Share } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { SummaryTab } from './SummaryTab';

// Lazy load heavy tabs to reduce initial bundle size
const RepurposeTab = React.lazy(() =>
  import(/* webpackChunkName: "repurpose-tab" */ './RepurposeTab').then((module) => ({
    default: module.RepurposeTab,
  }))
);

const ChatTab = React.lazy(() =>
  import(/* webpackChunkName: "chat-tab" */ './ChatTab').then((module) => ({
    default: module.ChatTab,
  }))
);

const MindmapTab = React.lazy(() =>
  import(/* webpackChunkName: "mindmap-tab" */ './MindmapTab').then((module) => ({
    default: module.MindmapTab,
  }))
);
import PDFModal from '../../../ui/PDFModal';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  copyToClipboard,
  exportAsTextFile,
  exportAsPDF,
  formatSummaryForExport,
  formatTranscriptForExport,
  generateFilename,
} from '../../../../lib/content-utils';
import { useToast, toast } from '@components/ui/toast';
import { useSummarizer } from '@core/store/useSummarizer';
import { useMediaQuery } from 'usehooks-ts';
import { getYouTubeVideoId } from '@/components/SuggestedVideos';

interface OutputTabsProps {
  summary: Summary;
  config?: SummarizerConfig; // Made optional since we'll use store config
  onBack: () => void;
  primaryColor?: string;
  className?: string;
}

type OutputTabType = 'summary' | 'repurpose' | 'chat' | 'mindmap' | 'transcript';

const OutputTabs: React.FC<OutputTabsProps> = ({ summary, config: propConfig }) => {
  const { currentConfig, setShowCTADialog, setCTADialogType, uploadedFile } = useSummarizer();

  // Use store config if available, otherwise fall back to prop config
  const config = currentConfig || propConfig;

  // Initialize with the first available tab based on config
  const getInitialTab = (): OutputTabType => {
    if (!config) return 'summary'; // fallback if no config available
    if (config?.outputs?.summary !== false) return 'summary';
    if (config?.outputs?.repurpose === true) return 'repurpose';
    if (config?.outputs?.chat !== false) return 'chat';
    if (config?.outputs?.mindmap !== false) return 'mindmap';
    if (config?.outputs?.transcript !== false && summary.transcript) return 'transcript';
    return 'summary'; // fallback
  };

  const [activeTab, setActiveTab] = useState<OutputTabType>(getInitialTab());
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const { addToast } = useToast();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isLarge = useMediaQuery('(min-width: 1024px)');
  const [isPDFModalOpen, setIsPDFModalOpen] = useState(false);

  // Sidebar width state management
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('summary-sidebar-width');
    return saved ? parseInt(saved) : 400; // Default width in pixels
  });

  const sidebarConfig = {
    min: 300,
    max: 800,
    value: sidebarWidth,
  };

  // Check if source is PDF
  const isPdfSource = useMemo(() => {
    return config?.api?.contentType === 'pdf' || summary.source === 'PDF';
  }, [config, summary.source]);

  // Define all possible tabs with their default properties
  const allTabs: {
    id: OutputTabType;
    label: string;
    icon: ReactNode;
    enabled: boolean;
    mobileOnly?: boolean;
  }[] = [
    {
      id: 'summary' as OutputTabType,
      label: config?.outputs?.summaryTabLabel || 'Summary',
      icon: <AlignLeft className="w-4 h-4" />,
      enabled: config?.outputs?.summary !== false,
    },
    {
      id: 'repurpose' as OutputTabType,
      label: 'Repurpose',
      icon: <Layers className="w-4 h-4" />,
      enabled: config?.outputs?.repurpose === true,
    },
    {
      id: 'chat' as OutputTabType,
      label: 'AI Chat',
      icon: <MessageSquare className="w-4 h-4" />,
      enabled: config?.outputs?.chat !== false,
    },
    {
      id: 'mindmap' as OutputTabType,
      label: 'Mind Map',
      icon: <Brain className="w-4 h-4" />,
      enabled: config?.outputs?.mindmap !== false,
    },
    {
      id: 'transcript' as OutputTabType,
      label: 'Transcript',
      icon: <FileText className="w-4 h-4" />,
      enabled: !!summary.transcript && config?.outputs?.transcript !== false,
      mobileOnly: false,
    },
  ];

  // Apply custom ordering if specified in config, otherwise use default order
  const customOrder = config?.outputs?.tabsOrder;
  const availableTabs =
    customOrder && customOrder.length > 0
      ? // Use custom order: map each ID to its tab definition, filter enabled
        customOrder
          .map((tabId) => allTabs.find((tab) => tab.id === tabId))
          .filter((tab): tab is NonNullable<typeof tab> => !!tab && tab.enabled)
          .concat(
            // Add any tabs not in custom order but still enabled
            allTabs.filter((tab) => tab.enabled && !customOrder.includes(tab.id))
          )
      : // Use default order: just filter enabled tabs
        allTabs.filter((tab) => tab.enabled);

  // Update active tab if config changes and current tab is no longer available
  useEffect(() => {
    const availableTabIds = availableTabs.map((tab) => tab.id);
    if (!availableTabIds.includes(activeTab)) {
      setActiveTab(getInitialTab());
    }
  }, [config, availableTabs.length]); // Re-run when config or available tabs change

  useEffect(() => {
    const checkOverflow = () => {
      if (tabsContainerRef.current) {
        const { scrollWidth, clientWidth } = tabsContainerRef.current;
        setShowScrollButtons(scrollWidth > clientWidth);
      }
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => {
      if (tabsContainerRef.current) {
        resizeObserver.unobserve(tabsContainerRef.current);
      }
    };
  }, [availableTabs]);

  const scroll = (scrollOffset: number) => {
    if (tabsContainerRef.current) {
      tabsContainerRef.current.scrollLeft += scrollOffset;
    }
  };

  // Sidebar resize handlers
  const sidebarMouseDownHandler = (e: React.MouseEvent) => {
    const startX = e.clientX;
    const startWidth = sidebarWidth;

    const mouseMoveHandler = (e: MouseEvent) => {
      const dx = e.clientX - startX;
      const newWidth = startWidth + dx;
      if (newWidth >= sidebarConfig.min && newWidth <= sidebarConfig.max) {
        localStorage.setItem('summary-sidebar-width', newWidth.toString());
        setSidebarWidth(newWidth);
      }
    };

    const mouseUpHandler = () => {
      document.removeEventListener('mouseup', mouseUpHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
    };

    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
  };

  // Copy handler for main summary
  const handleCopySummary = async () => {
    try {
      const textToCopy = formatSummaryForExport(summary);
      await copyToClipboard(textToCopy);
    } catch (error) {
      addToast(toast.error('Failed to copy summary'));
    }
  };

  // Copy handler for transcript
  const handleCopyTranscript = async () => {
    try {
      if (!summary.transcript) {
        return;
      }

      await copyToClipboard(summary.transcript);
    } catch (error) {
      addToast(toast.error('Failed to copy transcript'));
    }
  };

  // Export handler for main summary (TXT)
  const handleExportSummary = async () => {
    try {
      const content = formatSummaryForExport(summary);

      if (!content || content.length < 50) {
        return;
      }

      const filename = generateFilename(summary.title);
      exportAsTextFile(content, filename);
      addToast(toast.success('Summary exported as TXT successfully'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(toast.error(`Failed to export summary: ${errorMessage}`));
    }
  };

  // Export handler for summary (PDF)
  const handleExportSummaryAsPDF = async () => {
    try {
      const content = formatSummaryForExport(summary);

      if (!content || content.length < 50) {
        return;
      }

      const title = summary.title || 'Summary';
      const filename = generateFilename(summary.title, 'pdf');

      addToast(toast.info('Generating PDF... This may take a moment.'));
      await exportAsPDF(content, title, filename);
      addToast(toast.success('Summary exported as PDF successfully'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(toast.error(`Failed to export PDF: ${errorMessage}`));
    }
  };

  // Export handler for transcript (TXT)
  const handleExportTranscript = async () => {
    try {
      if (!summary.transcript) {
        return;
      }

      const content = formatTranscriptForExport(summary.transcript, summary.title);

      const filename = generateFilename(
        summary.title ? `${summary.title}_transcript` : 'transcript'
      );

      exportAsTextFile(content, filename);
      addToast(toast.success('Transcript exported as TXT successfully'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(toast.error(`Failed to export transcript: ${errorMessage}`));
    }
  };

  // Export handler for transcript (PDF)
  const handleExportTranscriptAsPDF = async () => {
    try {
      if (!summary.transcript) {
        return;
      }

      const content = formatTranscriptForExport(summary.transcript, summary.title);
      const title = summary.title ? `${summary.title} - Transcript` : 'Transcript';
      const filename = generateFilename(
        summary.title ? `${summary.title}_transcript` : 'transcript',
        'pdf'
      );

      addToast(toast.info('Generating PDF... This may take a moment.'));
      await exportAsPDF(content, title, filename);
      addToast(toast.success('Transcript exported as PDF successfully'));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      addToast(toast.error(`Failed to export PDF: ${errorMessage}`));
    }
  };

  // Share handler - opens CTA dialog
  const handleShare = () => {
    setCTADialogType('sign_up_required');
    setShowCTADialog(true);
  };

  // Add to collection handler - opens CTA dialog
  const handleAddToCollection = () => {
    setCTADialogType('sign_up_required');
    setShowCTADialog(true);
  };

  // PDF viewing handler
  const handleViewPDF = () => {
    setIsPDFModalOpen(true);
  };

  return (
    <div className="rounded-xl bg-white border mt-4 flex flex-col min-h-[400px] sm:!min-h-[500px] lg:!h-[calc(100vh-64px)] lg:!min-h-[300px]">
      {/* Header Section - Responsive height */}
      <div className="w-full min-h-[60px] lg:!h-16 flex items-center justify-between gap-3 sm:!gap-5 p-3 sm:!p-4 border-b flex-shrink-0">
        <p className="!text-[1.2rem] sm:!text-[1rem] lg:!text-[1.5rem] font-semibold text-gray-900 truncate flex-1">
          {config?.outputTitle || config?.title || 'Summary'}
        </p>
        <div className="flex items-center gap-2">
          {config?.outputs?.share !== false && (
            <Button
              variant="outline"
              className="w-10 h-10"
              onClick={handleShare}
              title="Share summary"
            >
              <Share className="w-4 h-4" />
            </Button>
          )}
          <Button variant="outline" className="h-10" onClick={handleAddToCollection}>
            <Plus className="w-4 h-4" />
            <span className="hidden sm:!inline">Add to your collection</span>
          </Button>
        </div>
      </div>

      {/* Main Content Area - Mobile responsive layout */}
      <div className="flex flex-col lg:!flex-row flex-1 overflow-hidden">
        {/* Sidebar - Mobile collapsible, desktop resizable */}
        <div
          className="border-b md:border-b-0 md:border-r p-4 flex flex-col overflow-hidden"
          style={{ width: isLarge ? `${sidebarWidth}px` : '100%' }}
        >
          {/* Content - Always visible on desktop, collapsible on mobile */}
          <div className={cn('flex-1 flex flex-col overflow-hidden')}>
            {false && isPdfSource ? (
              /* PDF Viewer for PDF sources */
              <>
                <div className="flex items-center justify-between min-h-10 mb-3 flex-shrink-0">
                  <div className="text-sm font-medium">PDF Content</div>
                  <div className="flex gap-1 sm:!gap-2 items-center">
                    <CopyButton
                      onCopy={handleCopySummary}
                      size="icon"
                      className="w-7 h-7 sm:!w-8 sm:!h-8"
                    />
                    {config?.outputs?.download && (
                      <ExportDropdown
                        onExportTxt={handleExportSummary}
                        onExportPdf={handleExportSummaryAsPDF}
                        variant="outline"
                        size="icon"
                        className="w-7 h-7 sm:!w-8 sm:!h-8"
                        showText={false}
                        title="Export summary"
                      />
                    )}
                  </div>
                </div>
                {isMobile ? (
                  /* Mobile: Show View PDF button instead of embedded viewer */
                  <div className="flex-1 min-h-[200px] bg-gray-50 rounded-lg flex flex-col items-center justify-center p-4">
                    <div className="text-center mb-4">
                      <div className="text-4xl mb-2">📄</div>
                      <p className="text-sm text-gray-600 mb-1">PDF Document</p>
                      <p className="text-xs text-gray-500">Tap to view in full screen</p>
                    </div>
                    <Button
                      onClick={handleViewPDF}
                      className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-3"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      View PDF
                    </Button>
                  </div>
                ) : (
                  /* Desktop: Show embedded PDF viewer */
                  <div className="flex-1 min-h-[200px] lg:!min-h-0">
                    {uploadedFile || summary.metadata?.url ? (
                      <PDFViewer
                        pdfFile={uploadedFile || undefined}
                        pdfUrl={summary.metadata?.url || undefined}
                        title={summary.title}
                        className="w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-50 rounded-lg flex flex-col items-center justify-center p-8">
                        <div className="text-center">
                          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <p className="text-lg font-medium text-gray-900 mb-2">
                            PDF Not Available
                          </p>
                          <p className="text-sm text-gray-600 mb-4 max-w-sm">
                            The PDF document is currently unavailable for preview. You can still
                            view the summary and other content below.
                          </p>
                          <div className="text-xs text-gray-500">
                            Original source: {summary.title}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Default: Thumbnail + Transcript for other sources */
              <>
                {/* Thumbnail - Responsive */}
                <div className="w-full flex-shrink-0 mb-3">
                  {summary.source?.toLowerCase() === 'youtube' &&
                  (summary.metadata?.url || summary.metadata?.youtube_video_id) ? (
                    <div className="flex-none w-full aspect-video cursor-pointer transition-shadow duration-300 hover:shadow-xl relative">
                      <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${summary.metadata.youtube_video_id || getYouTubeVideoId(summary.metadata.url || '')}?controls=0`}
                        title={summary.title}
                        style={{ border: 0 }}
                        className="rounded-lg shadow-md pointer-events-none"
                      ></iframe>
                    </div>
                  ) : summary.thumbnail || summary.thumbnail_url ? (
                    <SafeImage
                      className="min-h-[160px] sm:!min-h-[200px] lg:!min-h-[200px] object-cover rounded-lg w-full"
                      src={summary.thumbnail_url || summary.thumbnail || ''}
                      alt=""
                      fallback={
                        <div className="h-[160px] sm:!h-[200px] lg:!h-[200px] rounded-lg bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-500 text-sm">No thumbnail available</span>
                        </div>
                      }
                    />
                  ) : null}
                </div>
                {/* Title - Responsive */}
                {summary.title && (
                  <p className="text-sm sm:!text-base font-medium mb-3 flex-shrink-0 line-clamp-2">
                    {summary.title}
                  </p>
                )}
                {/* Transcript Section - Hidden on mobile (use transcript tab instead) */}
                {config?.outputs?.transcript && (
                  <div className="hidden lg:!flex border-t pt-3 flex-1 flex-col overflow-hidden">
                    <div className="flex items-center justify-between min-h-10 mb-3 flex-shrink-0">
                      <div className="text-sm font-medium">Transcript</div>
                      <div className="flex gap-2 items-center">
                        <CopyButton onCopy={handleCopyTranscript} size="icon" className="w-8 h-8" />
                        {config?.outputs?.download !== false && (
                          <ExportDropdown
                            onExportTxt={handleExportTranscript}
                            onExportPdf={handleExportTranscriptAsPDF}
                            variant="outline"
                            size="icon"
                            className="w-8 h-8"
                            showText={false}
                            title="Export transcript"
                          />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      <p className="text-sm text-gray-500 leading-relaxed">{summary.transcript}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Resize Handle */}
        <div className="relative hidden w-3 bg-transparent lg:!block">
          <div className="h-full w-[1px] bg-gray-300"></div>
          <div
            className="absolute -left-1 top-1/2 -translate-y-1/2 cursor-col-resize"
            onMouseDown={sidebarMouseDownHandler}
          >
            <div className="h-8 w-2 rounded-[30px] border border-gray-300 bg-gray-300 hover:bg-gray-400 transition-colors dark:border-gray-600 dark:bg-gray-600 dark:hover:bg-gray-500"></div>
          </div>
        </div>

        {/* Main Content Area - Flexible width */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tab Navigation - Mobile scrollable */}
          <div className="relative flex-shrink-0">
            {showScrollButtons && (
              <Button
                variant="outline"
                size="icon"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 sm:!h-8 sm:!w-8 rounded-full"
                onClick={() => scroll(-100)}
              >
                <ChevronLeft className="w-3 h-3 sm:!w-4 sm:!h-4" />
              </Button>
            )}
            <div
              ref={tabsContainerRef}
              className="flex items-center gap-1 sm:!gap-2 p-3 sm:!p-4 border-b overflow-x-auto scrollbar-hide"
            >
              {availableTabs.map((tab) => {
                if (!tab.enabled || (!isMobile && tab.mobileOnly)) {
                  return null;
                }

                return (
                  <Button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    variant="ghost"
                    className={cn(
                      '!bg-[#EFEFEF] w-full hover:bg-gray-300 !text-gray-900 font-normal min-h-[36px] sm:!h-10 px-2 sm:!px-3 text-xs sm:!text-sm transition-colors whitespace-nowrap',
                      activeTab === tab.id &&
                        '!bg-sky-500 hover:!bg-sky-500 !text-white hover:!text-white'
                    )}
                  >
                    <span>{tab.icon}</span>
                    <span className="hidden sm:!inline-block">{tab.label}</span>
                  </Button>
                );
              })}
            </div>
            {showScrollButtons && (
              <Button
                variant="outline"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 sm:!h-8 sm:!w-8 rounded-full"
                onClick={() => scroll(100)}
              >
                <ChevronRight className="w-3 h-3 sm:!w-4 sm:!h-4" />
              </Button>
            )}
          </div>

          {/* Tab Content - Mobile optimized scrollable area */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'summary' && config?.outputs?.summary !== false && (
              <SummaryTab summary={summary} config={config} />
            )}
            {activeTab === 'repurpose' && config?.outputs?.repurpose === true && (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                }
              >
                <RepurposeTab />
              </Suspense>
            )}
            {activeTab === 'chat' && config?.outputs?.chat !== false && (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                }
              >
                <ChatTab summary={summary} />
              </Suspense>
            )}
            {activeTab === 'mindmap' && config?.outputs?.mindmap !== false && (
              <Suspense
                fallback={
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                }
              >
                <MindmapTab summary={summary} />
              </Suspense>
            )}
            {activeTab === 'transcript' && config?.outputs?.transcript !== false && (
              <div className="p-3 sm:!p-4">
                {/* Mobile transcript header */}
                <div className="flex items-center justify-between mb-4 lg:!hidden">
                  <p className="text-base font-medium text-gray-900">Transcript</p>
                  <div className="flex gap-2 items-center">
                    <CopyButton onCopy={handleCopyTranscript} size="icon" className="w-8 h-8" />
                    {config?.outputs?.download !== false && (
                      <ExportDropdown
                        onExportTxt={handleExportTranscript}
                        onExportPdf={handleExportTranscriptAsPDF}
                        variant="outline"
                        size="icon"
                        className="w-8 h-8"
                        showText={false}
                        title="Export transcript"
                      />
                    )}
                  </div>
                </div>

                {/* Transcript content */}
                <div className="prose prose-sm max-w-none">
                  <div className="bg-gray-50 rounded-lg p-3 sm:!p-4">
                    <div className="whitespace-pre-wrap text-sm leading-relaxed text-gray-700 font-sans">
                      {summary.transcript || 'No transcript available'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PDF Modal for mobile viewing */}
      <PDFModal
        isOpen={isPDFModalOpen}
        onClose={() => setIsPDFModalOpen(false)}
        pdfFile={uploadedFile || undefined}
        pdfUrl={summary.metadata?.url}
        title={summary.title}
      />
    </div>
  );
};

export default OutputTabs;
